/**
 * Permission codes referenced by the UI.
 *
 * The canonical, complete list lives in zelushr-api:
 * `src/module/Common/Authorization/permissions.ts` (rbac-seed).
 * This module only mirrors the codes the UI actually references —
 * add codes here as new areas become permission-gated.
 */
export const PERMISSIONS = {
  APPROVAL: {
    REQUEST: {
      APPROVE: 'approval:request:approve',
      REJECT: 'approval:request:reject',
      // Gates the worklist itself (GET /approval/pending); APPROVE/REJECT gate the row actions.
      VIEW: 'approval:request:view',
    },
  },
  DEVICE: {
    ASSET: {
      CREATE: 'device:asset:create',
      DELETE: 'device:asset:delete',
      READ: 'device:asset:read',
      UPDATE: 'device:asset:update',
    },
    ASSIGNMENT: {
      CREATE: 'device:assignment:create',
      READ: 'device:assignment:read',
      UPDATE: 'device:assignment:update',
    },
    CATEGORY: {
      CREATE: 'device:category:create',
      DELETE: 'device:category:delete',
      READ: 'device:category:read',
      UPDATE: 'device:category:update',
    },
  },
  DOCUMENT: {
    FILE: {
      DELETE: 'document:file:delete',
      UPDATE: 'document:file:update',
    },
  },
  EMPLOYEE: {
    // Pay data, so READ is self-only — it reaches the caller's own entries via
    // the `/search` routes and nothing else. Every company-wide surface (the
    // admin allowance/deduction lists, refunds, installment lookups) needs
    // READ_ALL.
    ALLOWANCE_DEDUCTION: {
      CREATE: 'employee:allowance_deduction:create',
      DELETE: 'employee:allowance_deduction:delete',
      READ: 'employee:allowance_deduction:read',
      READ_ALL: 'employee:allowance_deduction:read_all',
      REFUND: 'employee:allowance_deduction:refund',
      UPDATE: 'employee:allowance_deduction:update',
    },
    // The plain codes are self-service — they only reach the caller's own
    // dependents. Anything company-wide (the admin list, the employee picker
    // on the form) needs the matching `*_all` code.
    DEPENDENT: {
      CREATE: 'employee:dependent:create',
      CREATE_ALL: 'employee:dependent:create_all',
      DELETE: 'employee:dependent:delete',
      DELETE_ALL: 'employee:dependent:delete_all',
      READ: 'employee:dependent:read',
      READ_ALL: 'employee:dependent:read_all',
      UPDATE: 'employee:dependent:update',
      UPDATE_ALL: 'employee:dependent:update_all',
    },
    ORG_CHART: {
      READ: 'employee:org_chart:read',
    },
    // The company roster (name, empId, photo, status) behind every employee
    // picker. Separate from PROFILE.READ_ALL, which is the full HR record.
    DIRECTORY: {
      READ: 'employee:directory:read',
    },
    PAY: {
      READ: 'employee:pay:read',
      READ_ALL: 'employee:pay:read_all',
      UPDATE: 'employee:pay:update',
    },
    PROFILE: {
      CREATE: 'employee:profile:create',
      DELETE: 'employee:profile:delete',
      READ: 'employee:profile:read',
      READ_ALL: 'employee:profile:read_all',
      REJOIN: 'employee:profile:rejoin',
      SEPARATE: 'employee:profile:separate',
      UPDATE: 'employee:profile:update',
      // Required to PUT another employee's profile; plain UPDATE reaches own only.
      UPDATE_ALL: 'employee:profile:update_all',
    },
  },
  PAY: {
    GRADE: {
      CREATE: 'pay:grade:create',
      READ: 'pay:grade:read',
      UPDATE: 'pay:grade:update',
      // Authority, not scope: CREATE/UPDATE propose a change into the approval
      // queue, APPROVE applies it immediately.
      APPROVE: 'pay:grade:approve',
    },
    // READ is self-only; READ_ALL is required to see anyone else's loans.
    // MANAGE_ANY is write breadth — one code lifts create/update/delete onto
    // another employee's loan, and it also hard-gates recording a payment.
    LOAN: {
      CREATE: 'pay:loan:create',
      READ: 'pay:loan:read',
      READ_ALL: 'pay:loan:read_all',
      UPDATE: 'pay:loan:update',
      DELETE: 'pay:loan:delete',
      MANAGE_ANY: 'pay:loan:manage_any',
    },
    PAYROLL: {
      APPROVE: 'pay:payroll:approve',
      HOLD: 'pay:payroll:hold',
      READ: 'pay:payroll:read',
      READ_ALL: 'pay:payroll:read_all',
      RUN: 'pay:payroll:run',
    },
    SCHEDULE: {
      CLOSE: 'pay:schedule:close',
      CREATE: 'pay:schedule:create',
      DELETE: 'pay:schedule:delete',
    },
  },
  RECRUITMENT: {
    DASHBOARD: {
      READ: 'recruitment:dashboard:read',
    },
    HIRING_TEAM: {
      MANAGE: 'recruitment:hiring_team:manage',
      READ: 'recruitment:hiring_team:read',
    },
    SETTINGS: {
      MANAGE: 'recruitment:settings:manage',
      READ: 'recruitment:settings:read',
    },
    APPLICANT: {
      CREATE: 'recruitment:applicant:create',
      DELETE: 'recruitment:applicant:delete',
      READ: 'recruitment:applicant:read',
      // Application notes on every job, not only the caller's hiring teams.
      READ_ALL: 'recruitment:applicant:read_all',
      UPDATE: 'recruitment:applicant:update',
    },
    INTERVIEW: {
      READ: 'recruitment:interview:read',
      SCHEDULE: 'recruitment:interview:schedule',
    },
    JOB: {
      CREATE: 'recruitment:job:create',
      DELETE: 'recruitment:job:delete',
      PUBLISH: 'recruitment:job:publish',
      READ: 'recruitment:job:read',
      UPDATE: 'recruitment:job:update',
    },
    OFFER: {
      APPROVE: 'recruitment:offer:approve',
      CREATE: 'recruitment:offer:create',
      DELETE: 'recruitment:offer:delete',
      READ: 'recruitment:offer:read',
      UPDATE: 'recruitment:offer:update',
    },
    SCORECARD: {
      MANAGE: 'recruitment:scorecard:manage',
      READ: 'recruitment:scorecard:read',
    },
  },
  REPORT: {
    FAVORITE: {
      MANAGE: 'report:favorite:manage',
    },
    ATTENDANCE: {
      GENERATE: 'report:attendance:generate',
    },
    RECRUITMENT: {
      GENERATE: 'report:recruitment:generate',
    },
    DASHBOARD: {
      READ: 'report:dashboard:read',
    },
    EMPLOYEE: {
      GENERATE: 'report:employee:generate',
    },
    LEAVE: {
      GENERATE: 'report:leave:generate',
    },
    PAYROLL: {
      GENERATE: 'report:payroll:generate',
    },
  },
  SYSTEM: {
    CONFIG: {
      READ: 'system:config:read',
      MANAGE: 'system:config:manage',
    },
    DEPARTMENT: {
      MANAGE: 'system:department:manage',
    },
    DESIGNATION: {
      MANAGE: 'system:designation:manage',
    },
    LEAVE_TYPE: {
      MANAGE: 'system:leave_type:manage',
      READ: 'system:leave_type:read',
    },
    LOCATION: {
      MANAGE: 'system:location:manage',
    },
    PERMISSION: {
      READ: 'system:permission:read',
    },
    ROLE: {
      CREATE: 'system:role:create',
      READ: 'system:role:read',
      UPDATE: 'system:role:update',
    },
    ROLE_ASSIGNMENT: {
      MANAGE: 'system:role_assignment:manage',
      READ: 'system:role_assignment:read',
    },
    USER: {
      CREATE: 'system:user:create',
      READ: 'system:user:read',
      UPDATE: 'system:user:update',
    },
  },
  TIME: {
    HOLIDAY: {
      CREATE: 'time:holiday:create',
      READ: 'time:holiday:read',
      UPDATE: 'time:holiday:update',
      DELETE: 'time:holiday:delete',
    },
    LEAVE: {
      APPROVE: 'time:leave:approve',
      // Team tier (direct reports). Gates use [ALL, TEAM] any-of — widest wins.
      APPROVE_TEAM: 'time:leave:approve_team',
      CREATE: 'time:leave:create',
      DELETE: 'time:leave:delete',
      READ: 'time:leave:read',
      READ_ALL: 'time:leave:read_all',
      READ_TEAM: 'time:leave:read_team',
      UPDATE: 'time:leave:update',
      WITHDRAW: 'time:leave:withdraw',
      // Same act-on-behalf-of axis as OVERTIME.MANAGE_ANY below.
      MANAGE_ANY: 'time:leave:manage_any',
      MANAGE_TEAM: 'time:leave:manage_team',
    },
    LEAVE_RECONCILIATION: {
      PROCESS: 'time:leave_reconciliation:process',
      READ: 'time:leave_reconciliation:read',
    },
    LEAVE_ACCRUAL: {
      ACCRUAL_RUN: 'time:leave_accrual:accrual_run',
      READ: 'time:leave_accrual:read',
    },
    LEAVE_BALANCE: {
      ADJUST: 'time:leave_balance:adjust',
      READ: 'time:leave_balance:read',
      // READ is the self code every employee holds — it does NOT reach the
      // all-employees roster. Gate roster pages on [READ_ALL, READ_TEAM].
      READ_ALL: 'time:leave_balance:read_all',
      READ_TEAM: 'time:leave_balance:read_team',
    },
    LEAVE_GRANT: {
      CREATE: 'time:leave_grant:create',
      // READ is self-scope (the caller's own grants); the company-wide list
      // and the admin Leave Grants page require READ_ALL.
      READ: 'time:leave_grant:read',
      READ_ALL: 'time:leave_grant:read_all',
      REVOKE: 'time:leave_grant:revoke',
    },
    LEAVE_POLICY: {
      CREATE: 'time:leave_policy:create',
      DELETE: 'time:leave_policy:delete',
      READ: 'time:leave_policy:read',
      UPDATE: 'time:leave_policy:update',
    },
    OVERTIME: {
      CREATE: 'time:overtime:create',
      DELETE: 'time:overtime:delete',
      READ: 'time:overtime:read',
      // Read breadth: without read_all every overtime read is scoped to the
      // caller's own records by the API.
      READ_ALL: 'time:overtime:read_all',
      UPDATE: 'time:overtime:update',
      WITHDRAW: 'time:overtime:withdraw',
      // Write breadth: one code lifts create/update/withdraw/delete onto another
      // employee's record. `_any` (act on behalf of) is a different axis from
      // `_all` (read breadth) — both suffixes coexist deliberately.
      MANAGE_ANY: 'time:overtime:manage_any',
      READ_TEAM: 'time:overtime:read_team',
    },
  },
} as const;

type Leaves<T> = T extends string ? T : { [K in keyof T]: Leaves<T[K]> }[keyof T];

export type PermissionCode = Leaves<typeof PERMISSIONS>;
